import styled, { css } from 'styled-components';
import { IconButton, Paper } from '../../design-system';
import { Close as CloseIcon, FilterAlt as FilterIcon } from '@mui/icons-material';
import { smallScreenSize, useCallBackOnBack } from '../../hooks';

export const DashboardWrapper = styled.div<{ showBottomNavigation?: boolean }>`
  display: grid;
  grid-template-rows: 1fr;

  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;

  ${(props) =>
    props.showBottomNavigation &&
    css`
      grid-template-rows: 1fr max-content;
    `}
`;

export const DashboardBase = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  grid-template-rows: max-content 1fr;

  height: 100%;
  overflow-x: hidden;
`;

export const DashboardMain = styled.div<{ pagename?: string }>`
  display: grid;
  grid-template-rows: 100%;
  overflow: overlay;

  @media (min-width: ${smallScreenSize + 1}px) {
    padding: var(--spacing-10);
    padding-top: var(--spacing-8);

    ${(props) =>
      props.pagename === 'overview' &&
      css`
        padding: var(--spacing-6);
      `}
  }

  @media (max-width: ${smallScreenSize}px) {
    position: relative;
    overflow-x: hidden;

    padding: var(--spacing-3) var(--spacing-6);
  }
`;

// REUSABLE COMPONENTS
export const DashboardHeader = styled.div<{ isSmallScreen?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-columns: max-content;
  grid-column-gap: var(--spacing-3);
  grid-auto-flow: column;
  align-items: center;
  align-content: center;

  position: relative;

  ${(props) =>
    props.isSmallScreen &&
    css`
      height: 3rem;
    `}

  @media print {
    & button {
      display: none;
    }
  }
`;

interface DashboardFooterProps {
  columns?: string;
}

export const DashboardFooter = styled(DashboardHeader)<DashboardFooterProps>`
  grid-template-columns: max-content;
  justify-content: end;

  ${(props) =>
    props.columns &&
    css`
      grid-template-columns: ${props.columns};
    `}
`;

export const DashboardFooterWithResize = styled(DashboardFooter)`
  @media (max-width: 1540px) {
    grid-auto-flow: row;
    grid-template-columns: 1fr;
    grid-row-gap: var(--spacing-3);
    & > button.MuiButtonBase-root {
      justify-self: initial;
    }
  }
`;

export const DashboardFormWrapper = styled.div<{ flow?: string; innerOverflow?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  grid-column-gap: 3rem;

  & > *:nth-child(1) {
    height: max-content !important;
  }

  ${(props) =>
    props.flow === 'row' &&
    css`
      grid-auto-rows: max-content;
      grid-auto-flow: row;
      grid-row-gap: 3rem;
    `}

  ${(props) =>
    props.innerOverflow &&
    css`
      overflow: hidden;

      & > *:nth-child(1) {
        height: 100%;
      }
    `}

  @media (min-width: ${smallScreenSize + 1}px) {
    padding-bottom: var(--spacing-10);
  }

  @media (max-width: ${smallScreenSize}px) {
    padding-bottom: var(--spacing-3);
  }

  @media (max-width: ${smallScreenSize}px) {
    ${(props) =>
      props.flow === 'row' &&
      css`
        grid-row-gap: var(--spacing-3);
      `}
  }
`;

export const DashboardForm = styled(Paper)<{ withoutspacing?: string }>`
  display: grid;
  grid-auto-rows: max-content;
  grid-auto-flow: row;
  grid-row-gap: var(--spacing-6);
  min-width: 73rem;
  overflow: visible;

  ${(props) =>
    !!props.withoutspacing &&
    css`
      grid-row-gap: 0;
    `}
`;

export const DashboardFormCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: max-content;
  grid-gap: var(--spacing-6);

  @media (max-width: ${smallScreenSize}px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: 1fr 1fr;
  }
`;

export const DashboardFormThreeCards = styled(DashboardFormCards)`
  grid-column: 1 / 3;
  grid-template-columns: 1fr 1fr 1fr;
`;

interface DashboardFormCardProps {
  rows?: string;
}

export const DashboardFormCard = styled(Paper)<DashboardFormCardProps>`
  display: grid;
  grid-auto-rows: max-content;
  grid-auto-flow: row;
  overflow: visible;

  ${(props) =>
    props.rows &&
    css`
      grid-template-rows: ${props.rows};
    `}
`;

export const DashboardFormCardRows = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-auto-flow: row;
  overflow: visible;

  grid-row-gap: var(--spacing-6);
`;

export const DashboardFormCardsFooter = styled.div`
  grid-column: 1 / 3;
`;

export const DashboardFormTwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: max-content;
  grid-column-gap: 2.8rem;
  grid-row-gap: 2.4rem;
`;

interface DashboardFormCustomColumnTypes {
  columns: string;
  bottomSpacing?: boolean;
  align?: string;
  alignContent?: string;
  withoutGap?: boolean;
}

export const DashboardFormCustomColumn = styled(
  DashboardFormTwoColumn,
)<DashboardFormCustomColumnTypes>`
  grid-template-columns: ${(props) => props.columns};
  align-items: ${(props) => props.align};
  align-content: ${(props) => props.alignContent};

  ${(props) =>
    props.bottomSpacing &&
    css`
      margin-bottom: var(--spacing-6);
    `}

  ${(props) =>
    props.withoutGap &&
    css`
      grid-gap: initial;
    `}

  & .row-button-square {
    margin-top: var(--spacing-6);
  }
`;

const DashboardIconButtonBase = styled(IconButton)`
  height: max-content;
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`;

interface FilterIconButtonProps {
  onClick: () => void;
}

export const FilterIconButton: React.FC<FilterIconButtonProps> = ({
  onClick,
}: FilterIconButtonProps) => {
  return (
    <DashboardIconButtonBase id={'filter-icon-button'} onClick={onClick} iconColor={'--gray-90'}>
      <FilterIcon />
    </DashboardIconButtonBase>
  );
};

export const CloseIconButton: React.FC<FilterIconButtonProps> = ({
  onClick,
}: FilterIconButtonProps) => {
  useCallBackOnBack(onClick);

  return (
    <DashboardIconButtonBase onClick={onClick} iconColor={'--gray-90'}>
      <CloseIcon />
    </DashboardIconButtonBase>
  );
};
